import { Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { ROUTES } from "@routing/routes";
import { currencyFormatter } from "@utils/formatter";
import { hasNoLocationMembershipAtom } from "@/store/shipToAtoms";
import { excludedCaveat } from "@customer/_helpers/noLocationCopy";
import { CHECKOUT_UNAVAILABLE_REASON } from "../_helpers/checkoutUnavailableCopy";
import { outOfStockReason, overStockReason } from "../_helpers/stockCopy";
import { FreeShippingMeter } from "./FreeShippingMeter";

interface OrderSummaryProps {
    subtotal: number;
    itemCount: number;
    /**
     * Lines left out of `subtotal` because they can't ship to the selected location
     * (#228 / E4-S12). The summary is sticky and, on mobile, renders ABOVE the item
     * list and its notice — so the number has to carry its own caveat, and the
     * free-shipping meter below has to be read against it.
     */
    excludedCount: number;
    /**
     * No public payment keys (#429): checkout can place no order at all, so the way
     * there is closed and the reason is shown beside the control.
     */
    checkoutUnavailable: boolean;
    /**
     * Lines above their vendor's known stock (#443 / C-07): checkout refuses the whole
     * order (412) while there is one, so the way there is closed, with the reason.
     */
    overStockCount: number;
    /**
     * Lines no live vendor offers (PO ruling 2026-11-02): they can't be ordered, so the
     * way to checkout is closed, with the reason.
     */
    noOfferCount: number;
    /**
     * Whether the way to checkout is closed — computed ONCE by the Cart (restricted
     * lines, no payment keys, lines above stock, lines no vendor offers) and shared with
     * its mobile control, so
     * the two can never disagree. The props above only supply the reasons shown.
     */
    checkoutClosed: boolean;
    freeShippingThreshold: number;
}

export const OrderSummary = ({
    subtotal,
    itemCount,
    excludedCount,
    checkoutUnavailable,
    overStockCount,
    noOfferCount,
    checkoutClosed,
    freeShippingThreshold,
}: OrderSummaryProps) => {
    // #417. Read here, not passed in, so no caller can forget it.
    const hasNoLocationMembership = useAtomValue(hasNoLocationMembershipAtom);

    return (
        <div className="bg-base-100 rounded-lg p-4 md:p-5 flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-3">
                <p className="text-sm md:text-base text-neutral">
                    <span className="text-neutral/70">Subtotal</span>
                    <span className="text-neutral/70 ml-1">
                        ({itemCount} {itemCount === 1 ? "Item" : "Items"})
                    </span>
                </p>
                <p className="text-lg md:text-xl font-bold text-neutral">
                    {currencyFormatter.format(subtotal)}
                </p>
            </div>

            {excludedCount > 0 && (
                <p
                    data-testid="order-summary-excluded"
                    className="-mt-2 text-sm text-neutral/70"
                >
                    {excludedCaveat(excludedCount, hasNoLocationMembership)}
                </p>
            )}

            {checkoutUnavailable && (
                <p
                    data-testid="order-summary-checkout-unavailable"
                    className="-mt-2 text-sm font-semibold text-error"
                >
                    {CHECKOUT_UNAVAILABLE_REASON}
                </p>
            )}

            {overStockCount > 0 && (
                <p
                    data-testid="order-summary-over-stock"
                    className="-mt-2 text-sm font-semibold text-error"
                >
                    {overStockReason(overStockCount)}
                </p>
            )}

            {noOfferCount > 0 && (
                <p
                    data-testid="order-summary-out-of-stock"
                    className="-mt-2 text-sm font-semibold text-error"
                >
                    {outOfStockReason(noOfferCount)}
                </p>
            )}

            {/* Checkout refuses the whole order while a restricted line, or a line
                above its vendor's stock (#443), is in the cart (412), and can place
                none at all without payment keys (#429), so the way there is closed
                until that changes — the line above says why. A disabled control,
                not a live link that lands on a page whose Place Order is disabled
                (or refused) for the same reason. */}
            {checkoutClosed ? (
                <button
                    type="button"
                    disabled
                    className="btn btn-warning w-full text-neutral font-bold"
                >
                    Checkout
                </button>
            ) : (
                <Link
                    to={ROUTES.CHECKOUT}
                    className="btn btn-warning w-full text-neutral font-bold"
                >
                    Checkout
                </Link>
            )}

            {freeShippingThreshold > 0 && (
                <FreeShippingMeter
                    subtotal={subtotal}
                    threshold={freeShippingThreshold}
                />
            )}

            <Link
                to={ROUTES.BROWSE}
                className="btn btn-outline w-full text-neutral border-neutral hover:bg-base-200"
            >
                Continue Shopping
            </Link>
        </div>
    );
};
